import React, { useMemo, useState } from 'react';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import { Card, Typography, Table, Space, Tag, Button, Statistic, Row, Col, Slider, Tooltip } from 'antd';
import { DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';

const { Title, Text, Paragraph } = Typography;

interface PeptidePosition {
  startPos: number;
  sequence: string;
  mlScore: number;
  blastScore: number;
  totalScore: number;
  prediction: string;
  seqId: string;
  windowLength: number;
}

interface EpitopeRegion {
  key: string;
  start: number;
  end: number;
  length: number;
  sequence: string;
  avgScore: number;
  maxScore: number;
  peptideCount: number;
}

const CLBTopeResult: React.FC<ResultComponentProps> = ({
  data,
  title,
  onDeleteResult,
  multiResults
}) => {
  const [chartThreshold, setChartThreshold] = useState(0.5);

  const parsedData = useMemo(() => {
    console.log('[CLBTopeResult] === RAW DATA ===');
    console.log('[CLBTopeResult] data type:', typeof data);
    console.log('[CLBTopeResult] data keys:', data ? Object.keys(data) : 'null/undefined');
    console.log('[CLBTopeResult] data.results?', data?.results ? `array[${data.results.length}]` : 'no');
    if (data?.results?.[0]) {
      console.log('[CLBTopeResult] data.results[0] keys:', Object.keys(data.results[0]));
      console.log('[CLBTopeResult] data.results[0].result keys:', data.results[0].result ? Object.keys(data.results[0].result) : 'no result');
      console.log('[CLBTopeResult] data.results[0].result.raw_predictions?', 
        data.results[0].result?.raw_predictions ? `array[${data.results[0].result.raw_predictions.length}]` : 'no');
      console.log('[CLBTopeResult] data.results[0].result.top_epitopes?',
        data.results[0].result?.top_epitopes ? `array[${data.results[0].result.top_epitopes.length}]` : 'no');
    }
    console.log('[CLBTopeResult] data.result?', data?.result ? 'yes' : 'no');
    console.log('[CLBTopeResult] data.success?', data?.success);
    console.log('[CLBTopeResult] data.error?', data?.error);
    console.log('[CLBTopeResult] === FULL DATA (first 2000 chars) ===');
    console.log(JSON.stringify(data)?.slice(0, 2000));
    
    let epitopeData = data;
    
    if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
      epitopeData = data.results[0].result;
    } else if (data && data.result) {
      epitopeData = data.result;
    }
    
    const rawPredictions = epitopeData?.raw_predictions || epitopeData?.predictions || [];
    const topEpitopes = epitopeData?.top_epitopes || [];
    const summaryStats = epitopeData?.summary_stats || {};
    
    return {
      raw_predictions: rawPredictions,
      top_epitopes: topEpitopes,
      summary_stats: summaryStats
    };
  }, [data]);

  const isScanMode = useMemo(() => {
    if (parsedData.raw_predictions.length === 0) return false;
    const first = parsedData.raw_predictions[0];
    return first.Pattern_ID !== undefined;
  }, [parsedData.raw_predictions]);

  const isDesignMode = useMemo(() => {
    if (parsedData.raw_predictions.length === 0) return false;
    const first = parsedData.raw_predictions[0];
    if (first.Pattern_ID !== undefined) return false;
    return first.Mutant_ID !== undefined;
  }, [parsedData.raw_predictions]);

  const predictionMode = useMemo(() => {
    if (isScanMode) return 'scan';
    if (isDesignMode) return 'design';
    return 'predict';
  }, [isScanMode, isDesignMode]);

  const predictChartData = useMemo(() => {
    if (isScanMode || parsedData.raw_predictions.length === 0) return null;
    
    if (isDesignMode) {
      const original = parsedData.raw_predictions.find((item: any) => 
        item.Mutant_ID === 'Original' || item.Mutant_ID?.toLowerCase() === 'original'
      );
      const originalScore = original ? (typeof original.Total_Score === 'number' ? original.Total_Score : 0) : 0;
      
      const mutants = parsedData.raw_predictions
        .filter((item: any) => item.Mutant_ID && item.Mutant_ID !== 'Original' && item.Mutant_ID.toLowerCase() !== 'original')
        .map((item: any) => ({
          mutantId: item.Mutant_ID || '',
          sequence: item.Sequence || '',
          mlScore: typeof item.ML_Score === 'number' ? item.ML_Score : 0,
          blastScore: typeof item.BLAST_Score === 'number' ? item.BLAST_Score : 0,
          totalScore: typeof item.Total_Score === 'number' ? item.Total_Score : 0,
          prediction: item.Prediction || '',
          scoreDiff: (typeof item.Total_Score === 'number' ? item.Total_Score : 0) - originalScore
        }))
        .sort((a: any, b: any) => b.totalScore - a.totalScore)
        .slice(0, 50);

      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: any) => {
            const item = mutants[params[0].dataIndex];
            if (!item) return '';
            return `
              <div style="font-size:12px; max-width:350px">
                <strong>突变:</strong> ${item.mutantId}<br/>
                <strong>序列:</strong> <span style="font-family:monospace">${item.sequence}</span><br/>
                <strong>Total Score:</strong> ${item.totalScore.toFixed(3)} (Δ${item.scoreDiff >= 0 ? '+' : ''}${item.scoreDiff.toFixed(3)})<br/>
                <strong>ML Score:</strong> ${item.mlScore.toFixed(3)}<br/>
                <strong>BLAST Score:</strong> ${item.blastScore.toFixed(3)}<br/>
                <strong>预测:</strong> ${item.prediction}
              </div>
            `;
          }
        },
        legend: {
          data: ['Total Score', '原始序列'],
          top: 0
        },
        grid: {
          left: '10%',
          right: '4%',
          bottom: '15%',
          top: '12%'
        },
        xAxis: {
          type: 'category',
          data: mutants.map((item: any) => item.mutantId),
          axisLabel: {
            rotate: 45,
            interval: 0,
            fontSize: 9
          }
        },
        yAxis: {
          type: 'value',
          name: '得分',
          nameLocation: 'middle',
          nameGap: 40
        },
        dataZoom: mutants.length > 20 ? [
          {
            type: 'slider',
            show: true,
            xAxisIndex: [0],
            start: 0,
            end: 30
          }
        ] : [],
        series: [
          {
            name: 'Total Score',
            type: 'bar',
            data: mutants.map((item: any) => ({
              value: item.totalScore,
              itemStyle: {
                color: item.scoreDiff > 0 ? '#52c41a' : (item.scoreDiff < 0 ? '#ff4d4f' : '#d9d9d9')
              }
            })),
            barMaxWidth: 20
          },
          {
            name: '原始序列',
            type: 'line',
            data: mutants.map(() => originalScore),
            lineStyle: { width: 2, color: '#faad14', type: 'dashed' },
            itemStyle: { color: '#faad14' },
            symbol: 'none'
          }
        ]
      };
    }
    
    const sorted = [...parsedData.raw_predictions]
      .map((item: any, index: number) => ({
        seqId: item.Seq_ID || `Seq${index + 1}`,
        sequence: item.Sequence || '',
        mlScore: typeof item.ML_Score === 'number' ? item.ML_Score : 0,
        blastScore: typeof item.BLAST_Score === 'number' ? item.BLAST_Score : 0,
        totalScore: typeof item.Total_Score === 'number' ? item.Total_Score : 0,
        prediction: item.Prediction || ''
      }))
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, 50);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const item = sorted[params[0].dataIndex];
          return `
            <div style="font-size:12px; max-width:300px">
              <strong>ID:</strong> ${item.seqId}<br/>
              <strong>序列:</strong> <span style="font-family:monospace">${item.sequence}</span><br/>
              <strong>ML Score:</strong> ${item.mlScore.toFixed(3)}<br/>
              <strong>BLAST Score:</strong> ${item.blastScore.toFixed(3)}<br/>
              <strong>Total Score:</strong> ${item.totalScore.toFixed(3)}<br/>
              <strong>预测:</strong> ${item.prediction}
            </div>
          `;
        }
      },
      legend: {
        data: ['Total Score', 'ML Score'],
        top: 0
      },
      grid: {
        left: '10%',
        right: '4%',
        bottom: '15%',
        top: '12%'
      },
      xAxis: {
        type: 'category',
        data: sorted.map((item: any) => item.seqId.length > 10 ? item.seqId.slice(0, 10) + '...' : item.seqId),
        axisLabel: {
          rotate: 45,
          interval: 0,
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        name: '得分',
        nameLocation: 'middle',
        nameGap: 40
      },
      dataZoom: sorted.length > 20 ? [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 40
        }
      ] : [],
      series: [
        {
          name: 'Total Score',
          type: 'bar',
          data: sorted.map((item: any) => ({
            value: item.totalScore,
            itemStyle: {
              color: item.prediction.toLowerCase().includes('epitope') ? '#ff4d4f' : '#d9d9d9'
            }
          })),
          barMaxWidth: 30
        },
        {
          name: 'ML Score',
          type: 'line',
          data: sorted.map((item: any) => item.mlScore),
          lineStyle: { width: 2, color: '#1890ff' },
          itemStyle: { color: '#1890ff' },
          symbol: 'circle',
          symbolSize: 6
        }
      ]
    };
  }, [isScanMode, parsedData.raw_predictions]);

  const peptidePositions: PeptidePosition[] = useMemo(() => {
    if (!isScanMode || parsedData.raw_predictions.length === 0) return [];
    return parsedData.raw_predictions.map((item: any, index: number) => {
      const patternMatch = item.Pattern_ID?.match(/(\d+)/);
      const patternNum = patternMatch ? parseInt(patternMatch[1]) : index + 1;
      return {
        startPos: patternNum,
        sequence: item.Sequence || '',
        mlScore: typeof item.ML_Score === 'number' ? item.ML_Score : 0,
        blastScore: typeof item.BLAST_Score === 'number' ? item.BLAST_Score : 0,
        totalScore: typeof item.Total_Score === 'number' ? item.Total_Score : 0,
        prediction: item.Prediction || '',
        seqId: item.Seq_ID || '',
        windowLength: (item.Sequence || '').length
      };
    });
  }, [parsedData.raw_predictions, isScanMode]);

  const epitopeRegions = useMemo(() => {
    if (peptidePositions.length === 0) return [];
    
    const epitopePeptides = peptidePositions.filter(
      p => p.totalScore >= chartThreshold && p.prediction.toLowerCase().includes('epitope')
    );
    
    if (epitopePeptides.length === 0) return [];
    
    const sorted = [...epitopePeptides].sort((a, b) => a.startPos - b.startPos);
    
    const regions: EpitopeRegion[] = [];
    let regionStart = sorted[0].startPos;
    let regionEnd = sorted[0].startPos + sorted[0].windowLength - 1;
    let regionPeptides = [sorted[0]];
    
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const currentEnd = current.startPos + current.windowLength - 1;
      
      if (current.startPos <= regionEnd + 1) {
        regionEnd = Math.max(regionEnd, currentEnd);
        regionPeptides.push(current);
      } else {
        const scores = regionPeptides.map(p => p.totalScore);
        const seq = regionPeptides.map(p => p.sequence).join('');
        regions.push({
          key: `region-${regions.length}`,
          start: regionStart,
          end: regionEnd,
          length: regionEnd - regionStart + 1,
          sequence: seq.length > 60 ? seq.substring(0, 60) + '...' : seq,
          avgScore: scores.reduce((a, b) => a + b, 0) / scores.length,
          maxScore: Math.max(...scores),
          peptideCount: regionPeptides.length
        });
        
        regionStart = current.startPos;
        regionEnd = currentEnd;
        regionPeptides = [current];
      }
    }
    
    const scores = regionPeptides.map(p => p.totalScore);
    const seq = regionPeptides.map(p => p.sequence).join('');
    regions.push({
      key: `region-${regions.length}`,
      start: regionStart,
      end: regionEnd,
      length: regionEnd - regionStart + 1,
      sequence: seq.length > 60 ? seq.substring(0, 60) + '...' : seq,
      avgScore: scores.reduce((a, b) => a + b, 0) / scores.length,
      maxScore: Math.max(...scores),
      peptideCount: regionPeptides.length
    });
    
    return regions;
  }, [peptidePositions, chartThreshold]);

  const chartOption = useMemo(() => {
    if (peptidePositions.length === 0) return null;

    const xData = peptidePositions.map(p => p.startPos);
    const totalScores = peptidePositions.map(p => p.totalScore);
    const mlScores = peptidePositions.map(p => p.mlScore);
    const blastScores = peptidePositions.map(p => p.blastScore);
    const isEpitope = peptidePositions.map(p => 
      p.prediction.toLowerCase().includes('epitope') && p.totalScore >= chartThreshold
    );

    const barColors = isEpitope.map(e => e ? '#ff4d4f' : '#d9d9d9');

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const idx = params[0].dataIndex;
          const p = peptidePositions[idx];
          return `
            <div style="font-size:12px">
              <strong>起始位置:</strong> ${p.startPos}<br/>
              <strong>肽段序列:</strong> <span style="font-family:monospace">${p.sequence}</span><br/>
              <strong>ML Score:</strong> ${p.mlScore.toFixed(3)}<br/>
              <strong>BLAST Score:</strong> ${p.blastScore.toFixed(3)}<br/>
              <strong>Total Score:</strong> ${p.totalScore.toFixed(3)}<br/>
              <strong>预测:</strong> ${p.prediction}
            </div>
          `;
        }
      },
      legend: {
        data: ['Total Score', 'ML Score', 'BLAST Score'],
        top: 0
      },
      grid: {
        left: '8%',
        right: '4%',
        bottom: '15%',
        top: '12%'
      },
      xAxis: {
        type: 'category',
        data: xData,
        name: '肽段起始位置',
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: {
          interval: Math.max(1, Math.floor(xData.length / 20)) - 1
        }
      },
      yAxis: {
        type: 'value',
        name: '得分',
        nameLocation: 'middle',
        nameGap: 45,
        min: 0
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          xAxisIndex: [0],
          start: 0,
          end: 100
        }
      ],
      series: [
        {
          name: 'Total Score',
          type: 'bar',
          data: totalScores.map((v: number, i: number) => ({
            value: v,
            itemStyle: { color: barColors[i] }
          })),
          barMaxWidth: 8
        },
        {
          name: 'ML Score',
          type: 'line',
          data: mlScores,
          lineStyle: { width: 1.5, color: '#1890ff' },
          itemStyle: { color: '#1890ff' },
          symbol: 'none',
          smooth: true
        },
        {
          name: 'BLAST Score',
          type: 'line',
          data: blastScores,
          lineStyle: { width: 1.5, color: '#52c41a', type: 'dashed' },
          itemStyle: { color: '#52c41a' },
          symbol: 'none',
          smooth: true
        },
        {
          name: '阈值',
          type: 'line',
          data: xData.map(() => chartThreshold),
          lineStyle: { width: 2, color: '#ff4d4f', type: 'dashed' },
          itemStyle: { color: '#ff4d4f' },
          symbol: 'none',
          markLine: {
            silent: true,
            data: [
              {
                yAxis: chartThreshold,
                lineStyle: { color: '#ff4d4f', type: 'dashed', width: 2 },
                label: { formatter: `阈值: ${chartThreshold.toFixed(2)}`, position: 'end' }
              }
            ]
          }
        }
      ]
    };
  }, [peptidePositions, chartThreshold]);

  const epitopeColumns = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: '序列',
      dataIndex: 'sequence',
      key: 'sequence',
      width: 300,
      render: (text: string) => (
        <Text 
          style={{ 
            fontFamily: 'monospace',
            fontWeight: 'bold',
            color: '#ff4d4f',
            backgroundColor: '#fff2f0',
            padding: '4px 8px',
            borderRadius: '4px',
            display: 'inline-block'
          }}
        >
          {text}
        </Text>
      )
    },
    {
      title: '长度',
      dataIndex: 'length',
      key: 'length',
      width: 80,
      sorter: (a: any, b: any) => a.length - b.length
    },
    {
      title: 'ML 分数',
      dataIndex: 'ml_score',
      key: 'ml_score',
      width: 100,
      render: (score: number) => typeof score === 'number' ? score.toFixed(3) : 'N/A',
      sorter: (a: any, b: any) => (a.ml_score || 0) - (b.ml_score || 0)
    },
    {
      title: 'BLAST 分数',
      dataIndex: 'blast_score',
      key: 'blast_score',
      width: 120,
      render: (score: number) => typeof score === 'number' ? score.toFixed(3) : 'N/A',
      sorter: (a: any, b: any) => (a.blast_score || 0) - (b.blast_score || 0)
    },
    {
      title: '总分',
      dataIndex: 'score',
      key: 'score',
      width: 100,
      render: (score: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {typeof score === 'number' ? score.toFixed(3) : 'N/A'}
        </Text>
      ),
      sorter: (a: any, b: any) => (a.score || 0) - (b.score || 0),
      defaultSortOrder: 'descend' as const
    },
    {
      title: 'Pattern ID',
      dataIndex: 'pattern_id',
      key: 'pattern_id',
      width: 120,
      render: (text: string) => text || 'N/A'
    }
  ];

  const rawColumns = [
    {
      title: '序列 ID',
      dataIndex: 'Seq_ID',
      key: 'seq_id',
      width: 150
    },
    {
      title: 'Pattern ID',
      dataIndex: 'Pattern_ID',
      key: 'pattern_id',
      width: 120
    },
    {
      title: '序列',
      dataIndex: 'Sequence',
      key: 'sequence',
      width: 250,
      render: (text: string) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'ML 分数',
      dataIndex: 'ML_Score',
      key: 'ml_score',
      width: 100,
      render: (score: number) => typeof score === 'number' ? score.toFixed(3) : 'N/A'
    },
    {
      title: 'BLAST 分数',
      dataIndex: 'BLAST_Score',
      key: 'blast_score',
      width: 120,
      render: (score: number) => typeof score === 'number' ? score.toFixed(3) : 'N/A'
    },
    {
      title: '总分',
      dataIndex: 'Total_Score',
      key: 'total_score',
      width: 100,
      render: (score: number) => (
        <Text strong>{typeof score === 'number' ? score.toFixed(3) : 'N/A'}</Text>
      )
    },
    {
      title: '预测结果',
      dataIndex: 'Prediction',
      key: 'prediction',
      width: 150,
      render: (pred: string) => {
        const isEpitope = pred && pred.toLowerCase().includes('epitope');
        return (
          <Tag 
            icon={isEpitope ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
            color={isEpitope ? 'success' : 'default'}
          >
            {pred}
          </Tag>
        );
      }
    }
  ];

  const regionColumns = [
    {
      title: '区间',
      key: 'region',
      width: 120,
      render: (_: any, record: EpitopeRegion) => (
        <Text strong>{record.start}-{record.end}</Text>
      )
    },
    {
      title: '长度',
      dataIndex: 'length',
      key: 'length',
      width: 80,
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => a.length - b.length
    },
    {
      title: '肽段数',
      dataIndex: 'peptideCount',
      key: 'peptideCount',
      width: 80,
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => a.peptideCount - b.peptideCount
    },
    {
      title: '表位序列',
      dataIndex: 'sequence',
      key: 'sequence',
      render: (text: string) => (
        <Text 
          style={{ 
            fontFamily: 'monospace', 
            fontWeight: 'bold',
            color: '#ff4d4f',
            backgroundColor: '#fff2f0',
            padding: '2px 4px',
            borderRadius: '2px'
          }}
        >
          {text}
        </Text>
      )
    },
    {
      title: '平均得分',
      dataIndex: 'avgScore',
      key: 'avgScore',
      width: 120,
      render: (score: number) => typeof score === 'number' ? score.toFixed(3) : 'N/A',
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => (a.avgScore || 0) - (b.avgScore || 0)
    },
    {
      title: '最高得分',
      dataIndex: 'maxScore',
      key: 'maxScore',
      width: 120,
      render: (score: number) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          {typeof score === 'number' ? score.toFixed(3) : 'N/A'}
        </Text>
      ),
      sorter: (a: EpitopeRegion, b: EpitopeRegion) => (a.maxScore || 0) - (b.maxScore || 0)
    }
  ];

  const exportData = () => {
    const lines: string[] = [];
    
    lines.push('=== Top 表位候选 ===');
    lines.push(['序号', '序列', '长度', 'ML分数', 'BLAST分数', '总分', 'Pattern ID'].join(','));
    parsedData.top_epitopes.forEach((item: any, index: number) => {
      lines.push([
        index + 1,
        item.sequence,
        item.length,
        item.ml_score?.toFixed(3) || 'N/A',
        item.blast_score?.toFixed(3) || 'N/A',
        item.score?.toFixed(3) || 'N/A',
        item.pattern_id || 'N/A'
      ].join(','));
    });
    
    if (epitopeRegions.length > 0) {
      lines.push('');
      lines.push('=== 表位区间 ===');
      lines.push(['区间', '长度', '肽段数', '平均得分', '最高得分'].join(','));
      epitopeRegions.forEach(region => {
        lines.push([
          `${region.start}-${region.end}`,
          region.length,
          region.peptideCount,
          region.avgScore.toFixed(3),
          region.maxScore.toFixed(3)
        ].join(','));
      });
    }
    
    lines.push('');
    lines.push('=== 统计信息 ===');
    lines.push(`总肽段数,${parsedData.summary_stats.total_peptides || 0}`);
    lines.push(`表位数,${parsedData.summary_stats.total_epitopes || 0}`);
    lines.push(`平均分数,${parsedData.summary_stats.mean_score?.toFixed(3) || 'N/A'}`);
    lines.push(`最高分数,${parsedData.summary_stats.max_score?.toFixed(3) || 'N/A'}`);
    lines.push(`高置信度表位数,${parsedData.summary_stats.high_confidence_epitopes || 0}`);
    
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `clbtope_epitopes_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasValidData = parsedData.raw_predictions.length > 0 || parsedData.top_epitopes.length > 0;
  
  const errorMessage = useMemo(() => {
    if (hasValidData) return null;
    
    const innerResult = data?.results?.[0]?.result;
    const innerError = data?.results?.[0]?.error;
    
    if (innerResult?.error) return innerResult.error;
    if (innerError) return innerError;
    if (data?.error) return data.error;
    if (data?.result?.error) return data.result.error;
    if (innerResult?.message && !innerResult?.success) return innerResult.message;
    if (data?.message && !data?.success) return data.message;
    
    return "无法显示表位预测结果，数据格式不正确或为空。";
  }, [hasValidData, data]);

  if (!hasValidData) {
    return (
      <BaseResultDisplay
        data={data}
        title={title || "CLBTope 表位预测结果"}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
      >
        <Text type="warning">{errorMessage}</Text>
      </BaseResultDisplay>
    );
  }

  return (
    <BaseResultDisplay
      data={data}
      title={title || "CLBTope 表位预测结果"}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Card size="small">
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="总肽段数" 
                value={parsedData.summary_stats.total_peptides || 0}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="表位数" 
                value={parsedData.summary_stats.total_epitopes || 0}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="平均分数" 
                value={parsedData.summary_stats.mean_score || 0}
                precision={3}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="最高分数" 
                value={parsedData.summary_stats.max_score || 0}
                precision={3}
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
          </Row>
        </Card>

        {isScanMode && chartOption && (
          <Card 
            title="表位预测可视化" 
            size="small"
            extra={
              <Space>
                <Text strong>阈值: {chartThreshold.toFixed(2)}</Text>
                <Slider 
                  min={0} 
                  max={1} 
                  step={0.01} 
                  value={chartThreshold} 
                  onChange={setChartThreshold}
                  style={{ width: 120 }}
                />
                <Tooltip title="红色柱状为超过阈值的表位肽段，灰色为非表位。蓝线为ML Score，绿虚线为BLAST Score。">
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            }
          >
            <ReactECharts
              option={chartOption}
              style={{ height: '400px', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              onChartReady={(chart: any) => {
                setTimeout(() => chart.resize(), 200);
              }}
            />
          </Card>
        )}

        {!isScanMode && predictChartData && (
          <Card 
            title={isDesignMode ? "突变体分数对比 (Top 50)" : "肽段分数排名 (Top 50)"}
            size="small"
            extra={
              <Tooltip title={isDesignMode ? 
                "红色为表位突变体，灰色为非表位。可帮助识别最佳突变位点。" :
                "红色为预测的表位肽段，灰色为非表位。按Total Score降序排列。"
              }>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            }
          >
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {isDesignMode ? 
                "Design模式：显示突变体的预测分数，帮助识别能增强表位活性的突变。" :
                "Predict模式：显示肽段的预测分数排名，红色为预测的B细胞表位。"
              }
            </Paragraph>
            <ReactECharts
              option={predictChartData}
              style={{ height: '400px', width: '100%' }}
              opts={{ renderer: 'canvas' }}
              onChartReady={(chart: any) => {
                setTimeout(() => chart.resize(), 200);
              }}
            />
          </Card>
        )}

        {isScanMode && epitopeRegions.length > 0 && (
          <Card title={`表位区间汇总 (共${epitopeRegions.length}个区间)`} size="small">
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              连续的表位肽段被合并为一个区间，区间长度 = 起始位置到结束位置覆盖的氨基酸数。
            </Paragraph>
            <Table
              columns={regionColumns}
              dataSource={epitopeRegions}
              pagination={false}
              size="small"
              scroll={{ x: 800 }}
            />
          </Card>
        )}

        {parsedData.top_epitopes.length > 0 && (
          <Card 
            title={`Top ${parsedData.top_epitopes.length} 表位候选`}
            size="small"
            extra={
              <Button 
                icon={<DownloadOutlined />}
                onClick={exportData}
                size="small"
              >
                导出CSV
              </Button>
            }
          >
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              以下是基于 CLBTope（Random Forest + BLAST）算法预测的高置信度B细胞表位候选。
            </Paragraph>
            <Table
              columns={epitopeColumns}
              dataSource={parsedData.top_epitopes}
              rowKey={(record, index) => `epitope-${index}`}
              pagination={false}
              size="small"
              scroll={{ x: 1000 }}
            />
          </Card>
        )}

        {parsedData.raw_predictions.length > 0 && (
          <Card 
            title="原始预测数据" 
            size="small"
            extra={
              <Text type="secondary">
                共 {parsedData.raw_predictions.length} 条记录
              </Text>
            }
          >
            <Table
              columns={rawColumns}
              dataSource={parsedData.raw_predictions}
              rowKey={(record, index) => `raw-${index}`}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              size="small"
              scroll={{ x: 1200 }}
            />
          </Card>
        )}

        <Card size="small" title="分数说明">
          <Space direction="vertical">
            <Text><strong>ML Score:</strong> 机器学习模型（Random Forest）预测分数</Text>
            <Text><strong>BLAST Score:</strong> BLAST 序列比对分数（与已知表位的相似度）</Text>
            <Text><strong>Total Score:</strong> ML Score + BLAST Score 的总分</Text>
            <Text><strong>Prediction:</strong> B-cell epitope（是表位） / Non B-cell epitope（非表位）</Text>
          </Space>
        </Card>
      </Space>
    </BaseResultDisplay>
  );
};

export default CLBTopeResult;
